import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Copy, EllipsisVertical, Key, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApi } from "@/hooks/useApi";
import { toast } from "@/hooks/use-toast";

interface KeyGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newKeyName: string;
  setNewKeyName: (name: string) => void;
  newlyGeneratedKey: string | null;
  setNewlyGeneratedKey: (key: string | null) => void;
  handleCreateKey: () => void;
}

const KeyGenerationDialog = ({
  open,
  onOpenChange,
  newKeyName,
  setNewKeyName,
  newlyGeneratedKey,
  setNewlyGeneratedKey,
  handleCreateKey,
}: KeyGenerationDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          {!newlyGeneratedKey ? "Generate New API Key" : "Your New API Key"}
        </DialogTitle>
        <DialogDescription>
          {!newlyGeneratedKey
            ? "Create a new API key for your application"
            : "Make sure to copy your API key - you won't see it again!"}
        </DialogDescription>
      </DialogHeader>

      {!newlyGeneratedKey ? (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="keyName" className="text-sm font-medium">
                Key Name
                <span className="text-red-500">*</span>
              </label>
              <input
                id="keyName"
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production API Key"
                className="w-full px-3 py-2 border rounded-md bg-white"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateKey}
              disabled={!newKeyName.trim()}
              className="flex items-center gap-2"
            >
              <Key size={16} />
              Generate Key
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="newApiKey" className="text-sm font-medium block">
                Your New API Key
              </label>
              <div className="relative">
                <pre
                  id="newApiKey"
                  className="bg-gray-100 p-3 rounded-md break-all text-sm text-pretty"
                >
                  {newlyGeneratedKey}
                </pre>
                <Button
                  size="sm"
                  variant="outline"
                  className="absolute right-2 top-2"
                  onClick={() =>
                    navigator.clipboard.writeText(newlyGeneratedKey)
                  }
                >
                  <Copy size={16} className="mr-2" />
                  Copy
                </Button>
              </div>
              <div className="mt-2 p-4 bg-yellow-50 text-yellow-800 rounded-md text-sm">
                <p className="font-medium">⚠️ Important:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Copy your API key now - it won't be shown again</li>
                  <li>Store it securely and don't share it publicly</li>
                  <li>
                    Use environment variables to store the key in your
                    application
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                setNewKeyName("");
                setNewlyGeneratedKey(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const ApiKeysTab = () => {
  const queryClient = useQueryClient();
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState<string | null>(
    null
  );

  const {
    apiKey: apiKeys,
    createApiAsync,
    deleteApiKeyAsync,
    isLoading,
    error,
  } = useApi();

  const handleCreateKey = async () => {
    try {
      const response = await createApiAsync({ project: newKeyName });
      setNewlyGeneratedKey(response.data.apiKey);
    } catch (error: any) {
      toast({
        title: "Failed to create API key",
        description: error?.response?.data?.message || error?.message || "Failed to generate API key. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading API keys: {error.message}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Keys</CardTitle>
        <CardDescription>
          Manage your API keys for authentication
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <Button
            type="button"
            onClick={() => setShowNewKeyDialog(true)}
            className="flex items-center gap-2"
          >
            <Key size={16} />
            Generate New API Key
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Created</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys?.data?.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell>{apiKey.project}</TableCell>
                  <TableCell>
                    <code className="bg-gray-100 px-2 py-1 rounded">
                      {`ms_.......${apiKey.suffix}`}
                    </code>
                  </TableCell>
                  <TableCell>
                    {new Date(apiKey.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0"
                        >
                          <span className="sr-only">Open menu</span>
                          <EllipsisVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={async () => {
                            try {
                              await deleteApiKeyAsync(apiKey.id, {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: ["apiKeys"],
                                  });
                                },
                              });
                              toast({
                                title: "API key deleted successfully",
                                description: `API key for ${apiKey.project} has been deleted successfully`,
                              });
                            } catch (error: any) {
                              toast({
                                title: "Failed to delete API key",
                                description: error?.response?.data?.message || error?.message || `API key for ${apiKey.project} might not have been deleted. Please try again later.`,
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Key
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <KeyGenerationDialog
          open={showNewKeyDialog}
          onOpenChange={setShowNewKeyDialog}
          newKeyName={newKeyName}
          setNewKeyName={setNewKeyName}
          newlyGeneratedKey={newlyGeneratedKey}
          setNewlyGeneratedKey={setNewlyGeneratedKey}
          handleCreateKey={handleCreateKey}
        />
      </CardContent>
    </Card>
  );
};

const ApiDocsPage = () => {
  const endpoints = [
    {
      method: "GET",
      path: "/api/documents",
      description: "Retrieve a list of documents",
      parameters: [
        {
          name: "page",
          type: "number",
          description: "Page number for pagination",
        },
        {
          name: "limit",
          type: "number",
          description: "Number of items per page",
        },
      ],
      response: {
        code: 200,
        body: `{
  "users": [
    {
      "id": 1,
      "name": "John Doe",
      "email": "john@example.com"
    }
  ],
  "total": 100,
  "page": 1
}`,
      },
    },
    {
      method: "POST",
      path: "/api/users",
      description: "Create a new user",
      parameters: [
        { name: "name", type: "string", description: "User's full name" },
        { name: "email", type: "string", description: "User's email address" },
      ],
      response: {
        code: 201,
        body: `{
  "id": 1,
  "name": "John Doe",
  "email": "john@example.com",
  "created_at": "2024-11-17T12:00:00Z"
}`,
      },
    },
  ];

  interface Endpoint {
    method: string;
    path: string;
    description: string;
    parameters: { name: string; type: string; description: string }[];
    response: { code: number; body: string };
  }

  const EndpointCard = ({ endpoint }: { endpoint: Endpoint }) => (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Badge
            className={
              endpoint.method === "GET" ? "bg-green-500" : "bg-blue-500"
            }
          >
            {endpoint.method}
          </Badge>
          <code className="text-sm">{endpoint.path}</code>
        </div>
        <CardDescription>{endpoint.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Parameters</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left">Name</th>
                    <th className="text-left">Type</th>
                    <th className="text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.parameters.map((param) => (
                    <tr key={param.name}>
                      <td className="py-2">{param.name}</td>
                      <td className="py-2">{param.type}</td>
                      <td className="py-2">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Response</h4>
            <div className="bg-gray-100 p-4 rounded-md relative">
              <button
                type="button"
                className="absolute right-2 top-2 p-1 hover:bg-gray-200 rounded"
                onClick={() =>
                  navigator.clipboard.writeText(endpoint.response.body)
                }
              >
                <Copy size={16} />
              </button>
              <pre className="text-sm overflow-x-auto">
                {endpoint.response.body}
              </pre>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-6 pl-12">
      <div className="max-w-5xl mr-auto">
        <h1 className="text-3xl font-bold mb-6">API Documentation</h1>

        <Tabs defaultValue="keys" className="space-y-6">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="keys">API Keys</TabsTrigger>
            <TabsTrigger value="authentication">Authentication</TabsTrigger>
            <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
            <TabsTrigger value="examples">Examples</TabsTrigger>
          </TabsList>

          <div className="w-full">
            <TabsContent value="keys">
              <ApiKeysTab />
            </TabsContent>

            <TabsContent value="authentication">
              <Card>
                <CardHeader>
                  <CardTitle>Authentication</CardTitle>
                  <CardDescription>
                    Learn how to authenticate your API requests
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    To authenticate your requests, include an API key in the
                    Authorization header:
                  </p>
                  <pre className="bg-gray-100 p-4 rounded-md">
                    Authorization: Bearer your-api-key-here
                  </pre>
                  <p>
                    You can generate and manage your API keys in the API Keys
                    tab. Keep your API keys secure and never share them
                    publicly.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="endpoints">
              <div className="space-y-6">
                {endpoints.map((endpoint) => (
                  <EndpointCard
                    key={`${endpoint.method}-${endpoint.path}`}
                    endpoint={endpoint}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="examples">
              <Card>
                <CardHeader>
                  <CardTitle>Code Examples</CardTitle>
                  <CardDescription>
                    Example code snippets in various programming languages
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">JavaScript</h4>
                      <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto">
                        {`fetch('https://api.example.com/documents', {
  headers: {
    'Authorization': 'Bearer your-api-key-here'
  }
})
.then(response => response.json())
.then(data => console.log(data));`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Python</h4>
                      <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto">
                        {`import requests

headers = {
    'Authorization': 'Bearer your-api-key-here'
}

response = requests.get('https://api.example.com/documents', headers=headers)
data = response.json()
print(data)`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">C#</h4>
                      <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto">
                        {`using System.Net.Http;
using System.Text.Json;

var client = new HttpClient();
client.DefaultRequestHeaders.Add("Authorization", "Bearer your-api-key-here");

var response = client.GetAsync("https://api.example.com/documents").Result;
var data = response.Content.ReadAsStringAsync().Result;
var json = JsonSerializer.Deserialize<dynamic>(data);

Console.WriteLine(json);`}
                      </pre>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default ApiDocsPage;
